/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../logging";
import { NetworkResource, NetworkResourceFailureReason } from "../../enums";
import {
  NetworkResourceNode,
  NodeStateChangeCallback
} from "../NetworkResourceNode";

/**
 * Default timeout for the HTTPS fallback probe request (in milliseconds).
 */
const PROBE_TIMEOUT_MS: number = 5000;

/**
 * Default HTTPS probing endpoint URL used for Internet reachability checks.
 * This is currently a temporary hardcoded value and should eventually be
 * replaced by a service-configurable probing endpoint.
 */
const DEFAULT_PROBE_URL: string =
  "https://connectivitycheck.gstatic.com/generate_204";

/**
 * Node tracking Internet reachability.
 */
class InternetNode extends NetworkResourceNode {
  protected static override readonly TAG: string = "InternetNode";

  /** AbortController for cancelling in-flight probe requests. */
  private mAbortController: AbortController | null;

  /** Whether probing is currently active. */
  private mIsProbing: boolean;

  /** Promise tracking the in-flight probe; null when idle. */
  private mProbePromise: Promise<void> | null;

  constructor(onStateChanged: NodeStateChangeCallback) {
    super(NetworkResource.INTERNET, onStateChanged);
    this.mAbortController = null;
    this.mIsProbing = false;
    this.mProbePromise = null;
  }

  /**
   * On init, Internet starts same as network interface node
   */
  public override init(): void {
    super.init();

    // TODO : In Unity 5.0 InternetNode = NetworkInterfaceNode
    // Log.i(
    //   InternetNode.TAG,
    //   "init() starting as unavailable (not yet evaluated)"
    // );
    const isConnected =
      typeof navigator !== "undefined" && navigator.onLine === true;
    Log.i(InternetNode.TAG, "init() initial onLine=" + String(isConnected));
    if (isConnected) this.setAvailability(true, null);
    else
      this.setAvailability(
        false,
        NetworkResourceFailureReason.NETWORK_INTERFACE_DOWN
      );
  }

  /**
   * Start active HTTPS probing to determine Internet connectivity.
   * Uses fetch with a timeout and AbortController for cancellation.
   * If a probe is already in progress, returns the existing probe promise
   * so the caller properly awaits the in-flight result.
   * @returns A promise that resolves when the probe completes
   */
  public override async invalidateState(): Promise<void> {
    if (this.mIsProbing && this.mProbePromise !== null) {
      Log.d(
        InternetNode.TAG,
        "invalidateState() probing already in progress — awaiting existing probe"
      );
      return this.mProbePromise;
    }

    Log.d(InternetNode.TAG, "invalidateState() starting Internet probe");
    this.mIsProbing = true;
    this.mProbePromise = this.performProbe();
    return this.mProbePromise;
  }

  /**
   * Cancel any in-flight probing and stop retry timers.
   */
  public override stopInvalidation(): void {
    Log.d(InternetNode.TAG, "stopInvalidation() cancelling probing");
    this.mIsProbing = false;
    this.mProbePromise = null;

    if (this.mAbortController !== null) {
      this.mAbortController.abort();
      this.mAbortController = null;
    }
  }

  /**
   * Handles external hints about Internet availability.
   * A hint of "unavailable" triggers re-evaluation via probing.
   * A hint of "available" is treated as definitive if not already available.
   * @param resource - The resource this hint pertains to
   * @param isAvailable - Whether the resource is believed available
   * @param reason - Failure reason if unavailable
   * @returns A promise that resolves when the hint has been fully processed
   */
  public override async onNetworkResourceAvailabilityHint(
    resource: NetworkResource,
    isAvailable: boolean,
    reason: NetworkResourceFailureReason | null
  ): Promise<void> {
    if (resource !== NetworkResource.INTERNET) {
      return;
    }

    Log.i(
      InternetNode.TAG,
      "onNetworkResourceAvailabilityHint() available=" +
        String(isAvailable) +
        ", reason=" +
        String(reason)
    );

    if (isAvailable) {
      this.stopInvalidation();
      this.setAvailability(true, null);
    } else {
      // TODO : In Unity 5.0 InternetNode = NetworkInterfaceNode, so using navigator status to set availability
      const isConnected =
        typeof navigator !== "undefined" && navigator.onLine === true;
      this.setAvailability(isConnected, isConnected ? null : reason);
    }
  }

  /**
   * Performs a single HTTPS probe. On success, marks Internet available.
   * On failure, marks unavailable. On abort (via stopInvalidation),
   * cleans up without changing availability since the PROPAGATE rule
   * already set the correct state via copyAvailabilityFrom.
   * @returns A promise that resolves when the probe attempt finishes
   */
  private async performProbe(): Promise<void> {
    const abortController: AbortController = new AbortController();
    this.mAbortController = abortController;
    const signal: AbortSignal = abortController.signal;

    const timeoutId: ReturnType<typeof setTimeout> = setTimeout(() => {
      abortController.abort();
    }, PROBE_TIMEOUT_MS);

    try {
      await fetch(DEFAULT_PROBE_URL, {
        method: "HEAD",
        mode: "no-cors",
        cache: "no-store",
        signal
      });

      clearTimeout(timeoutId);
      this.mAbortController = null;
      Log.i(InternetNode.TAG, "performProbe() success — Internet available");
      this.mIsProbing = false;
      this.mProbePromise = null;
      this.setAvailability(true, null);
    } catch (error: unknown) {
      clearTimeout(timeoutId);
      this.mAbortController = null;
      this.mIsProbing = false;
      this.mProbePromise = null;

      if (signal.aborted) {
        Log.d(InternetNode.TAG, "performProbe() aborted");
        return;
      }

      Log.w(
        InternetNode.TAG,
        "performProbe() failed — Internet unavailable",
        error
      );

      this.setAvailability(
        false,
        NetworkResourceFailureReason.INTERNET_UNREACHABLE
      );
    }
  }
}

export { InternetNode };
